<script>
   /*************************************************************/
   /* AppContainer for training                                 */
   /* -------------------------                                 */
   /* app container for training of DD-SIMCA model              */
   /*************************************************************/

   import { Container, Button } from 'svelte-controls-basic';
   import { downloadResults, downloadModel, NIMCA } from '../utils.svelte';

   import { excludeOutliers, genhash } from '@mdatools-apps/shared/utils';

   import Outliers from '@mdatools-apps/shared/widgets/Outliers.svelte';
   import PrepWidget from '@mdatools-apps/shared/components/prep/PrepWidget.svelte';

   import DataLoader from '@mdatools-apps/shared/components/pca/DataLoader.svelte';

   import ModelTable from '../shared/ModelTable.svelte';
   import NIMCASettings from '../shared/NIMCASettings.svelte';

   import ResultTable from './ResultTable.svelte';
   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // container for errors raised while applying the model to the training set
   let calerr = $state('');

   // get part of the session for this tab
   const s = $derived(session['train']);

   // if NIMCA model is not instance of NIMCA, recreate it
   $effect(() => {
      if (s.nimca && !(s.nimca instanceof NIMCA)) {
         s.nimca = NIMCA.fromJSON(s.nimca);
      }
   });


   // the model is fitted on the objects that are left - 'NIMCA.fit' is handed an already filtered
   // matrix - so it and everything derived from it go when the set of outliers changes. a
   // preprocessing model the user built here is derived from those objects too; one loaded from a
   // file is not, and nothing would bring it back, so it stays and PrepWidget reapplies it to the
   // new 'calseto'
   function invalidateForOutliers() {
      s.nimca = null;
      s.calres = null;
      s.calsetp = null;
      if (s.prepType === 'create') {
         s.prepmodel = null;
      }
   }


   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.calres.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.calseto.objindices[pos];

      // add the object to outliers array and reset the model and all related outcomes
      if (id >= 0 && !s.outliers.includes(id)) {
         s.nimca = null;
         s.outliers.push(id);
         s.calseto = excludeOutliers(s.calset, s.outliers);
         invalidateForOutliers();
      }
   }

   // callback to reset outliers
   function resetOutliers() {
      s.nimca = null;
      s.outliers = [];
      s.calseto = s.calset;
      invalidateForOutliers();
   }

   // callback to undo outliers
   function undoOutliers() {
      s.nimca = null;
      s.outliers.pop();
      s.calseto = excludeOutliers(s.calset, s.outliers);
      invalidateForOutliers();
   }

   // pre-rendering effect to avoid bad structure of the session object
   $effect.pre(() => {

      // if training set is set null - reset model and all related parameters
      if (s.calset === null) {
         s.outliers = [];
         s.calsetp = null;
         s.calseto = null;
         s.className = null;
         s.prepmodel = null;
         s.prepobj = [];
         s.prepType = null
         s.nimca = null;
         // the settings belonged to that set: a new one starts from the defaults
         s.center = true;
         s.scale = false;
         s.alpha = 5;
         s.gamma = 1;
         s.limType = 'classic';
      } else if (s.calset && !s.className) {
         s.className = s.calset.classNames[0];
         s.calseto = s.calset;
      }
   });

   // if preprocessed training set is set null - reset model and all related parameters
   $effect(() => {
      if (s.calsetp === null) {
         s.nimca = null;
         s.calres = null;
         // the message describes a result which no longer exists, and it is shown whether or not
         // there is a set on screen, so it has to go with the data it was about
         calerr = '';
      }
   });

   // 'calsetp' is rebuilt by PrepWidget whenever the objects or the preprocessing change, and a
   // fitted model has to be able to say which of those sets it came from. the set carries no id of
   // its own, so one is put on it the first time it is seen. this is a correctness identity and not
   // a display hash: 'genhash' alone is a timestamp and two sets stamped in the same millisecond
   // would share it. 'crypto.randomUUID' is not an option - it is secure-context only, and these
   // bundles run inside whatever page embeds them
   $effect.pre(() => {
      if (s.calsetp && !s.calsetp.nimcaCalsetToken) {
         s.calsetp.nimcaCalsetToken = genhash() + '-' + Math.random().toString(36).slice(2, 10);
      }
   });


   $effect(() => {
      if (s.calsetp && s.nimca) {
         try {
            s.calres = s.nimca.predict(s.calsetp, s.outliers);
            calerr = '';
         } catch (e) {
            // a stale result must not survive a failed prediction - it would stay on screen and
            // stay downloadable while describing data it was not computed from
            s.calres = null;
            calerr = e instanceof Error ? e.message : String(e);
         }
      }
   });

   // reactive effect for backing up the session. the session is read by 'backupSession' when the
   // write is due, not here, and the first run is skipped: it only ever coincides with a session
   // the app was given as a whole, which is already on disk, is written by 'MainApp' itself, or is
   // a fresh one that need not be stored. the values after the function are the dependencies (see
   // 'backupSession'). a change of alpha, gamma or the limits replaces no model, so without the
   // reads of the settings the app came back from a reload with the ones the user had left behind
   let mounted = false;
   $effect(() => {
      const deps = [s.calset, s.nimca, s.center, s.scale, s.alpha, s.gamma, s.limType];
      if (mounted) backupSession(() => session, ...deps);
      mounted = true;
   });

</script>


<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={s.calset} className="Class" classMandatory={true} classUnique={true}/>
      </div>

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.calset} {resetOutliers} {undoOutliers} />
      </div>
      {/if}

      {#if s.calseto }
         <PrepWidget
            {WASM}
            rawdata={s.calseto}
            bind:prepType={s.prepType}
            bind:prepobj={s.prepobj}
            bind:prepdata={s.calsetp}
            bind:prepmeta={s.prepmeta}
            bind:prepmodel={s.prepmodel}
         />
      {/if}


      <div class="nimca-settings">

         {#if s.calsetp }
         <NIMCASettings
            calset={s.calsetp}
            bind:nimca={s.nimca}
            bind:center={s.center}
            bind:scale={s.scale}
            bind:alpha={s.alpha}
            bind:gamma={s.gamma}
            bind:limType={s.limType}
         />
         {/if}

         <!-- error message -->
         {#if calerr}
         <Container status="error">
            <p>{calerr}</p>
         </Container>
         {/if}

         {#if s.nimca && s.calres}
         <Container label='Download'>
            <div class="buttons">
            <Button onclick={() => downloadResults(s.nimca, s.calres, s.calset, "train")} text="Res (cal)"/>
            <Button onclick={() => downloadModel(s.nimca, s.prepmodel)} text="Model" />
            </div>
         </Container>
         {/if}
      </div>

      {#if s.nimca && s.calres}
      <div class="model-stats">
         <ModelTable nimca={s.nimca} />
      </div>
      <div class="result-stats">
         <ResultTable calres={s.calres} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.nimca || !s.calres}
         <div class="help-text">
         {#if !s.calset}
            <p>
               In this tab you can train a Naïve DD-SIMCA (NIMCA) model. After creating the model you can save it to a file and use it in other tabs. For example, validate it using test set (tab <strong>Test</strong>) or apply it to a dataset with unknown class membership (tab <strong>Predict</strong>).
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load your training set from a CSV file. The file should contain data as numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows correspond to observations or objects, columns — to variables.
            </p>
            <p>
               First column must contain class names for each row. All rows in the training set should belong to the target class, so it is expected that the class names are the same (you will get error message if not).
            </p>
            <p>
               First row can contain variable names (<strong>header</strong>) and first column can contain labels for the objects/observations (<strong>row labels</strong>) — just set the corresponding options after selecting the file. In this case it is assumed that column with the class names is located next to the column with row labels. Here is an example:
            </p>
            <div>
               <table>
               <thead>
               <tr>
                  <td>Name</td>
                  <td>Class</td>
                  <td>x1</td>
                  <td>x2</td>
                  <td>x3</td>
               </tr>
               </thead>
               <tbody>
               <tr>
                  <td>O1</td>
                  <td>A</td>
                  <td>3.8</td>
                  <td>1.3</td>
                  <td>13.6</td>
               </tr>
               <tr>
                  <td>O2</td>
                  <td>A</td>
                  <td>11.1</td>
                  <td>12.3</td>
                  <td>3.3</td>
               </tr>
               <tr>
                  <td>O3</td>
                  <td>A</td>
                  <td>1.2</td>
                  <td>2.3</td>
                  <td>4.2</td>
               </tr>
               </tbody>
               </table>
            </div>
            <p></p>
         {:else}
            {#if !s.calsetp}
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to apply proper preprocessing methods. You can create a chain of the methods directly in the app
               (button <strong>Create</strong>), load previously created preprocessing model (<strong>Load</strong>), or just skip this step. In this case the data will be only mean centered for computing the distances.
            </p>
            {/if}
         {/if}
         </div>
      {:else if s.nimca && s.calres  }
         {#key s.calres }
         <AppPlots {markOutlier} {...s} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .model-stats,
   .app-controls > .result-stats {
      background: #e8e8e8;
   }

   .app-controls > .model-stats,
   .app-controls > .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table thead tr td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table tbody td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }



   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(325px, 30%) 1fr;
      }
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

</style>